import { ResourceType } from "@/game/entities/config/resources";
import useTileRepository from "@/hooks/useTileRepository";
import { type Price } from "@/types";
import { type TileCoordinates } from "@/types/tile";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";
import useSettlementsStore from "./useSettlementsStore";

export type MapExpansionConfig = {
	id: number;
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
	axis: "x" | "y";
	price: Price;
	quests: Array<{
		text: string;
		getTargetValue: () => number;
		getCurrentValue: () => number;
	}>;
	cache?: Array<{
		target?: number;
		current?: number;
	}>;
	isUnlocked: boolean;
	isReadyToUnlockByQuests: () => boolean;
	isReadyToUnlockByRegions: () => boolean;
};

const config: Array<Omit<MapExpansionConfig, "isUnlocked">> = [
	{
		id: 1,
		minX: 359 - 25,
		maxX: 359 - 1,
		minY: 359,
		maxY: 659,
		axis: "x", // po ktorej osi ide rozsirenie mapy
		price: {
			[ResourceType.COINS]: 1_000_000_000,
		},
		quests: [
			{
				text: "task.reach_level",
				getTargetValue: (): number => 50,
				getCurrentValue: (): number => useSettlementsStore().mainLevel,
			},
			{
				text: "task.have_explored_tiles",
				getTargetValue: (): number => 35_000,
				getCurrentValue: (): number =>
					useTileRepository().exploredTileCount,
			},
		],
		isReadyToUnlockByQuests: (): boolean => {
			const TARGET_REGION_ID = 1; // Toto musi byt rovnake ako id tejto map expansion.
			const store = useMapExpansionStore();
			return store[`${TARGET_REGION_ID}`].quests.every(
				(quest: MapExpansionConfig["quests"][number]): boolean =>
					quest.getCurrentValue() >= quest.getTargetValue(),
			);
		},
		isReadyToUnlockByRegions: (): boolean => true,
	},
	{
		id: 2,
		minX: 659 + 1,
		maxX: 659 + 25,
		minY: 359,
		maxY: 659,
		axis: "x",
		price: {
			[ResourceType.COINS]: 1_000_000_000,
		},
		quests: [
			{
				text: "task.reach_level",
				getTargetValue: (): number => 50,
				getCurrentValue: (): number => useSettlementsStore().mainLevel,
			},
			{
				text: "task.have_explored_tiles",
				getTargetValue: (): number => 35_000,
				getCurrentValue: (): number =>
					useTileRepository().exploredTileCount,
			},
		],
		isReadyToUnlockByQuests: (): boolean => {
			const TARGET_REGION_ID = 2; // Toto musi byt rovnake ako id tejto map expansion.
			const store = useMapExpansionStore();
			return store[`${TARGET_REGION_ID}`].quests.every(
				(quest: MapExpansionConfig["quests"][number]): boolean =>
					quest.getCurrentValue() >= quest.getTargetValue(),
			);
		},
		isReadyToUnlockByRegions: (): boolean => true,
	},
	{
		id: 3,
		minX: 359 - 25,
		maxX: 659 + 25,
		minY: 359 - 25,
		maxY: 359 - 1,
		axis: "y",
		price: {
			[ResourceType.COINS]: 25_000_000_000,
		},
		quests: [
			{
				text: "task.reach_level",
				getTargetValue: (): number => 60,
				getCurrentValue: (): number => useSettlementsStore().mainLevel,
			},
			{
				text: "task.have_explored_tiles",
				getTargetValue: (): number => 35_000,
				getCurrentValue: (): number =>
					useTileRepository().exploredTileCount,
			},
		],
		isReadyToUnlockByQuests: (): boolean => {
			const TARGET_REGION_ID = 3; // Toto musi byt rovnake ako id tejto map expansion.
			const store = useMapExpansionStore();
			return store[`${TARGET_REGION_ID}`].quests.every(
				(quest: MapExpansionConfig["quests"][number]): boolean =>
					quest.getCurrentValue() >= quest.getTargetValue(),
			);
		},
		isReadyToUnlockByRegions: (): boolean => {
			const REQUIRED_REGION_IDS = [1, 2];
			const store = useMapExpansionStore();
			return REQUIRED_REGION_IDS.every(
				(id: number): boolean => store[`${id}`].isUnlocked,
			);
		},
	},
	{
		id: 4,
		minX: 359 - 25,
		maxX: 659 + 25,
		minY: 659 + 1,
		maxY: 659 + 25,
		axis: "y",
		price: {
			[ResourceType.COINS]: 25_000_000_000,
		},
		quests: [
			{
				text: "task.reach_level",
				getTargetValue: (): number => 60,
				getCurrentValue: (): number => useSettlementsStore().mainLevel,
			},
			{
				text: "task.have_explored_tiles",
				getTargetValue: (): number => 35_000,
				getCurrentValue: (): number =>
					useTileRepository().exploredTileCount,
			},
		],
		isReadyToUnlockByQuests: (): boolean => {
			const TARGET_REGION_ID = 4; // Toto musi byt rovnake ako id tejto map expansion.
			const store = useMapExpansionStore();
			return store[`${TARGET_REGION_ID}`].quests.every(
				(quest: MapExpansionConfig["quests"][number]): boolean =>
					quest.getCurrentValue() >= quest.getTargetValue(),
			);
		},
		isReadyToUnlockByRegions: (): boolean => {
			const REQUIRED_REGION_IDS = [1, 2];
			const store = useMapExpansionStore();
			return REQUIRED_REGION_IDS.every(
				(id: number): boolean => store[`${id}`].isUnlocked,
			);
		},
	},
];

export type MapExpansionKey = `${number}`;

export type MapExpansionStoreState = Record<
	MapExpansionKey,
	MapExpansionConfig
>;

type MapExpansionStorePayload = {
	state: MapExpansionStoreState;
};

type MapExpansionStoreGetters = Record<
	MapExpansionKey,
	ComputedRef<MapExpansionConfig>
>;

type MapExpansionStore = MapExpansionStoreGetters & {
	$getPayload: () => MapExpansionStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<MapExpansionStoreState>) => void;
	keys: Array<MapExpansionKey>;
	isInLockedRegionByCoordinates: (coordinates: TileCoordinates) => boolean;
	getEffectiveMapBorderCoordinates: () => {
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
	};
	getMapExpansionByCoordinates: (
		coordinates: TileCoordinates,
	) => MapExpansionConfig | null;
	setIsUnlockedById: (id: number) => void;
};

const STORE_NAME = "map-expansion";

const useMapExpansionStore = defineStore(STORE_NAME, (): MapExpansionStore => {
	const state = createImmutableState<MapExpansionStoreState>(
		config.reduce(
			(
				acc: MapExpansionStoreState,
				config: Omit<MapExpansionConfig, "isUnlocked">,
			): MapExpansionStoreState => ({
				...acc,
				[`${config.id}`]: {
					...config,
					isUnlocked: false,
				},
			}),
			{} as MapExpansionStoreState,
		),
	);

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): MapExpansionStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<MapExpansionKey>;
		return keys.reduce(
			(
				acc: MapExpansionStoreState,
				key: MapExpansionKey,
			): MapExpansionStoreState => {
				if (
					Object.keys(data[key].cache || {}).length === 0 &&
					!data[key].isUnlocked
				)
					return acc;
				return {
					...acc,
					[key]: {
						...acc[key],
						cache: data[key].cache,
						isUnlocked: data[key].isUnlocked,
					},
				};
			},
			{} as MapExpansionStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: MapExpansionStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): MapExpansionStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<MapExpansionStoreState>): void => {
		const keys = Object.keys(value) as Array<MapExpansionKey>;
		for (const key of keys) {
			if (!state.value[key]) continue;
			setImmutableStateProperty(state, key, {
				...state.value[key],
				...value[key],
			});
		}
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const keys = config.map(
		(item: Omit<MapExpansionConfig, "isUnlocked">): MapExpansionKey =>
			`${item.id}`,
	) as Array<MapExpansionKey>;

	const getters = keys.reduce(
		(
			acc: MapExpansionStoreGetters,
			key: MapExpansionKey,
		): MapExpansionStoreGetters => ({
			...acc,
			[key]: getImmutableStateProperty(state, key, null!),
		}),
		{} as MapExpansionStoreGetters,
	);

	const isInLockedRegionByCoordinates = ({
		x,
		y,
	}: TileCoordinates): boolean => {
		return keys.some((key: MapExpansionKey): boolean => {
			const config = getters[key];
			return (
				config.value &&
				!config.value.isUnlocked &&
				x >= config.value.minX &&
				x <= config.value.maxX &&
				y >= config.value.minY &&
				y <= config.value.maxY
			);
		});
	};

	const getEffectiveMapBorderCoordinates = (): {
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
	} => {
		const array = new Array<TileCoordinates>(
			// Default mapa.
			{
				x: 359,
				y: 359,
			},
			{
				x: 359,
				y: 659,
			},
			{
				x: 659,
				y: 359,
			},
			{
				x: 659,
				y: 659,
			},
		);

		for (const key of keys) {
			const config = getters[key];
			if (!config.value?.isUnlocked) continue;

			array.push(
				{ x: config.value.minX, y: config.value.minY },
				{ x: config.value.minX, y: config.value.maxY },
				{ x: config.value.maxX, y: config.value.minY },
				{ x: config.value.maxX, y: config.value.maxY },
			);
		}

		const flatArray = array.flat();
		const xs = flatArray.map(({ x }: TileCoordinates): number => x);
		const ys = flatArray.map(({ y }: TileCoordinates): number => y);

		return {
			minX: Math.min(...xs),
			maxX: Math.max(...xs),
			minY: Math.min(...ys),
			maxY: Math.max(...ys),
		};
	};

	const getMapExpansionByCoordinates = ({
		x,
		y,
	}: TileCoordinates): MapExpansionConfig | null => {
		for (const key of keys) {
			const config = getters[key];
			if (!config.value) continue;
			if (!config.value.isReadyToUnlockByRegions()) continue;
			if (config.value.isUnlocked) continue;

			if (
				config.value &&
				x >= config.value.minX &&
				x <= config.value.maxX &&
				y >= config.value.minY &&
				y <= config.value.maxY
			)
				return config.value;
		}

		return null;
	};

	const setIsUnlockedById = (id: number): void => {
		const key = `${id}` as MapExpansionKey;
		const config = getters[key];
		if (!config.value) return;
		if (!config.value.isReadyToUnlockByRegions()) return;
		if (!config.value.isReadyToUnlockByQuests()) return;
		if (config.value.isUnlocked) return;

		setImmutableStateProperty(state, key, {
			...state.value[key],
			isUnlocked: true,
		});
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		keys,
		...getters,
		isInLockedRegionByCoordinates,
		getEffectiveMapBorderCoordinates,
		getMapExpansionByCoordinates,
		setIsUnlockedById,
	};
});

export default useMapExpansionStore;
